import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/slices/authSlice";
import { useGetAnalyticsQuery } from "../redux/services/api";
import Icon from "../components/Icon";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const Dashboard = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isArabic } = useLanguage();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState("today");

  // Use RTK Query for analytics
  const {
    data: analyticsResponse,
    isLoading: loading,
    refetch,
  } = useGetAnalyticsQuery();
  const analyticsData = analyticsResponse?.data || null;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getStats = () => {
    if (!analyticsData) {
      return [
        {
          title: t("dashboard.todaysVisitors"),
          value: "0",
          change: "0%",
          changeType: "neutral",
          icon: "users",
          color: "blue",
        },
        {
          title: t("dashboard.totalVisitors"),
          value: "0",
          change: "0%",
          changeType: "neutral",
          icon: "chart-bar",
          color: "green",
        },
        {
          title: t("dashboard.mostVisitedService"),
          value: t("dashboard.noData"),
          change: `0 ${t("dashboard.visits")}`,
          changeType: "neutral",
          icon: "cog",
          color: "purple",
        },
        {
          title: t("dashboard.mostVisitedOffer"),
          value: t("dashboard.noData"),
          change: `0 ${t("dashboard.visits")}`,
          changeType: "neutral",
          icon: "tag",
          color: "orange",
        },
      ];
    }

    return [
      {
        title: t("dashboard.todaysVisitors"),
        value: analyticsData.today_visitors?.toString() || "0",
        change: `${analyticsData.today_visitors || 0} ${t(
          "dashboard.uniqueVisitors"
        )}`,
        changeType: "positive",
        icon: "users",
        color: "blue",
      },
      {
        title: t("dashboard.totalVisitors"),
        value: analyticsData.total_visitors?.toString() || "0",
        change: `${t("dashboard.period")}: ${t(`dashboard.${selectedPeriod}`)}`,
        changeType: "positive",
        icon: "chart-bar",
        color: "green",
      },
      {
        title: t("dashboard.mostVisitedService"),
        value:
          analyticsData.most_visited_service?.title || t("dashboard.noData"),
        change: `${analyticsData.most_visited_service?.visits || 0} ${t(
          "dashboard.visits"
        )}`,
        changeType: analyticsData.most_visited_service ? "positive" : "neutral",
        icon: "cog",
        color: "purple",
      },
      {
        title: t("dashboard.mostVisitedOffer"),
        value: analyticsData.most_visited_offer?.title || t("dashboard.noData"),
        change: `${analyticsData.most_visited_offer?.visits || 0} ${t(
          "dashboard.visits"
        )}`,
        changeType: analyticsData.most_visited_offer ? "positive" : "neutral",
        icon: "tag",
        color: "orange",
      },
    ];
  };

  const quickActions = [
    {
      title: t("dashboard.homepageSettings"),
      description: t("dashboard.customizeHomepage"),
      icon: "home",
      color: "blue",
      link: "/dashboard/homepage",
    },
    {
      title: t("dashboard.addService"),
      description: t("dashboard.createNewService"),
      icon: "briefcase",
      color: "green",
      link: "/dashboard/services/add",
    },
    {
      title: t("dashboard.linkManagement"),
      description: t("dashboard.createAndTrackCustomLinks"),
      icon: "link",
      color: "blue",
      link: "/dashboard/links",
    },
    {
      title: t("dashboard.analytics"),
      description: t("dashboard.viewDetailedAnalytics"),
      icon: "chart-bar",
      color: "purple",
      link: "/dashboard/analytics",
    },
    {
      title: t("dashboard.settings"),
      description: t("dashboard.generalSystemSettings"),
      icon: "cog",
      color: "gray",
      link: "#",
    },
  ];

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour12: true,
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="dashboard-modern">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="welcome-section">
            <h1>
              {t("dashboard.welcomeBack")},{" "}
              {user?.name || t("dashboard.adminUser")}! 👋
            </h1>
            <p>{t("dashboard.heresWhatsHappening")}</p>
          </div>
          <div className="time-info">
            <div className="current-time">{formatTime(currentTime)}</div>
            <div className="current-date">{formatDate(currentTime)}</div>
          </div>
        </div>
        <div className="header-right">
          <div className="period-filter">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="period-select"
            >
              <option value="today">{t("dashboard.today")}</option>
              <option value="week">{t("dashboard.thisWeek")}</option>
              <option value="month">{t("dashboard.thisMonth")}</option>
              <option value="year">{t("dashboard.thisYear")}</option>
            </select>
          </div>
          <button
            className="view-homepage-btn"
            onClick={() => window.open("/", "_blank")}
          >
            <Icon name="eye" className="btn-icon" size="16px" />
            {t("dashboard.viewHomepage")}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid-modern">
        {getStats().map((stat, index) => (
          <div
            key={index}
            className={`stat-card-modern stat-${stat.color} ${
              stat.title === t("dashboard.todaysVisitors") ||
              stat.title === t("dashboard.totalVisitors")
                ? "clickable-stat"
                : ""
            }`}
            onClick={() => {
              if (stat.title === t("dashboard.todaysVisitors")) {
                navigate("/dashboard/visitor-details?type=today");
              } else if (stat.title === t("dashboard.totalVisitors")) {
                navigate("/dashboard/visitor-details?type=all");
              }
            }}
          >
            <div className="stat-header">
              <div className="stat-icon">
                <Icon name={stat.icon} size="24px" />
              </div>
              <div className={`stat-change ${stat.changeType}`}>
                {stat.change}
              </div>
            </div>
            <div className="stat-content">
              <h3 className="stat-title">{stat.title}</h3>
              <p className="stat-value">{stat.value}</p>
              {(stat.title === t("dashboard.todaysVisitors") ||
                stat.title === t("dashboard.totalVisitors")) && (
                <div className="stat-click-hint">
                  <Icon name="eye" size="14px" />
                  {t("dashboard.clickToViewDetails")}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-grid-modern">
        <div className="dashboard-card-modern">
          <div className="card-header-modern">
            <h3>
              <Icon name="cog" className="card-icon" size="20px" />
              {t("dashboard.quickActions")}
            </h3>
          </div>
          <div className="card-content-modern">
            <div className="quick-actions-modern">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  className={`action-card action-${action.color}`}
                  onClick={() => {
                    if (action.link === "#") {
                      // Handle non-functional links
                      console.log(
                        `${action.title} clicked - not implemented yet`
                      );
                    } else {
                      navigate(action.link);
                    }
                  }}
                >
                  <div className="action-icon">
                    <Icon name={action.icon} size="20px" />
                  </div>
                  <div className="action-content">
                    <h4>{action.title}</h4>
                    <p>{action.description}</p>
                  </div>
                  <div className="action-arrow">
                    <Icon
                      name={isArabic ? "arrowLeft" : "arrowRight"}
                      size="16px"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
